import { AddIcon } from '@chakra-ui/icons'
import { Box, Flex, IconButton, Text } from '@chakra-ui/react'
import { useApp } from '@hb/react'
import { LAB_RESULTS, LabResult, SaveLabResultArgs, USERS } from '@hb/shared'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useDocument } from '../../../../backend/db/hooks'
import { saveLabResult } from '../../../../backend/functions/gyn'
import { LabResultRow } from '../../../../charting/Pregnancy/LaborCharting/LabQuickview/LabResultRow'
import { SearchBarProvider } from '../../../../contexts/SearchBarContext'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useResizeObserver } from '../../../../hooks'
import { DataList } from '../../../DataList/DataList'
import { DefaultModal } from '../../../Modals/DefaultModal'
import { getLabResultsTab, LabResultItem } from './labResultsList'
import { LogLabResultForm } from './LogLabResultForm'

interface Props {
  assessmentId: string | null
  patientId: string
}

export const LabQuickview = ({ assessmentId, patientId }: Props) => {
  const { appName } = useApp()
  const { practiceId } = useProfile()
  const [isAdding, setIsAdding] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const handleSuccess = useCallback(() => setIsAdding(false), [])
  const handleCancel = useCallback(() => setIsAdding(false), [])
  const openForm = useCallback(() => setIsAdding(true), [])

  const bodyRef = useRef<HTMLDivElement>(null)
  const { width, height } = useResizeObserver(bodyRef, 'content')

  const tabs = useMemo(
    () => ({
      'Lab Results': getLabResultsTab(
        patientId,
        assessmentId,
        appName === 'providers-app' ? practiceId : null,
      ),
    }),
    [patientId, assessmentId, appName, practiceId],
  )

  const { data: selectedResult } = useDocument<LabResult>(
    `${USERS}/${patientId}/${LAB_RESULTS}`,
    selectedId,
  )
  const closeSelected = useCallback(() => setSelectedId(null), [])

  // Only pass through the update fields — spreading create-shaped defaults here would
  // overwrite labName/result/flagStatus on the existing document (merge-set backend).
  const handleSave = useCallback(
    async (args: Partial<SaveLabResultArgs> & { labResultId: string }) => {
      if (!practiceId) return
      await saveLabResult({
        assessmentId,
        patientId,
        practiceId,
        appName,
        ...args,
      } as SaveLabResultArgs)
    },
    [assessmentId, patientId, practiceId, appName],
  )

  return (
    <Flex
      w="100%"
      boxShadow="0 0 6px rgba(0,0,0,0.3)"
      flex={1}
      minH={0}
      direction="column"
      bg="white"
      borderRadius={4}>
      <Flex
        px={4}
        py={2}
        borderBottom="1px solid"
        borderColor="gray.200"
        bg="gray.50"
        align="center"
        flexShrink={0}>
        <Text fontFamily="Hero-New" fontWeight={600} fontSize="sm">
          {isAdding ? 'Log Lab Result' : 'Lab Results'}
        </Text>
        {!isAdding ? (
          <IconButton
            ml="auto"
            size="xs"
            colorScheme="blue"
            variant="ghost"
            aria-label="Log lab result"
            icon={<AddIcon />}
            onClick={openForm}
          />
        ) : null}
      </Flex>
      {isAdding ? (
        <Box flex={1} minH={0} overflowY="auto">
          <LogLabResultForm
            assessmentId={assessmentId}
            patientId={patientId}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
          />
        </Box>
      ) : (
        <Box flex={1} minH={0} ref={bodyRef}>
          {width && height ? (
            <SearchBarProvider>
              <DataList<LabResultItem>
                tabs={tabs}
                rootPath={`patients/${patientId}`}
                noShadow
                width={width}
                height={height}
                onRowClick={setSelectedId}
              />
            </SearchBarProvider>
          ) : null}
        </Box>
      )}
      {selectedId && selectedResult ? (
        <DefaultModal
          isOpen
          onClose={closeSelected}
          size="lg"
          header={selectedResult.labName || 'Lab Result'}
          render={() => (
            <Box p={3}>
              <LabResultRow result={{ ...selectedResult, id: selectedId }} onSave={handleSave} />
            </Box>
          )}
        />
      ) : null}
    </Flex>
  )
}
